import React, { FC, useState } from 'react'
import styled from 'styled-components'
// @ts-ignore
import { DashboardMapIcon } from 'bd-web-components'
import DonutIndicator, { DonutItem } from './DonutIndicator'
import {
  GeoCardContainer,
  GeoCol,
  GeoColTitle,
  GeoDivider,
  GeoMapBox,
  GeoMapRow,
  GeoStateCount,
  GeoStateList,
  GeoStateName,
  ModalOverlay,
  ModalPanel,
  ModalHead,
  ModalTitle,
  ModalClose,
} from './styles'

interface GeoSalesCardProps {
  /** resposta do salesLocator (mesma fonte do mapa) — cada estado traz cidades[] */
  data: any[]
  /** itens já agregados para a rosca de estados (tons de cinza) */
  donutItems: DonutItem[]
}

/** Lista rolável de todos os estados; cada um é clicável e abre o modal de cidades. */
const StateScroll = styled(GeoStateList)`
  gap: 6px;
  max-height: 300px;
  min-width: 160px;
  overflow-y: auto;
  padding-right: 8px;
`

const StateRow = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 9px;
  padding: 8px 10px;
  cursor: pointer;

  &:hover {
    background: #f4f6f9;
  }
`

/* -------- Modal de cidades -------- */
const CitySub = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #98a2b3;
  margin: 0 0 14px;
`

const CityList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 60vh;
  overflow-y: auto;
`

const CityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 4px;
  border-bottom: 1px solid #f2f4f7;

  &:last-child {
    border-bottom: none;
  }
`

const CityRank = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #98a2b3;
  width: 22px;
  flex-shrink: 0;
`

const CityName = styled.span`
  flex: 1;
  min-width: 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const CityCount = styled.span`
  flex-shrink: 0;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #101828;
  font-variant-numeric: tabular-nums;
`

const Empty = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #98a2b3;
  padding: 14px 0;
  text-align: center;
`

const GeoSalesCard: FC<GeoSalesCardProps> = ({ data, donutItems }) => {
  const [selected, setSelected] = useState<any | null>(null)

  const list = Array.isArray(data)
    ? data.filter((s: any) => s && typeof s.quantidadeIngressos === 'number')
    : []

  const ufs = list.map((s: any) => (s.uf === '' ? 'N/A' : s.uf))
  const states = [...list].sort(
    (a: any, b: any) => b.quantidadeIngressos - a.quantidadeIngressos
  )

  const cities =
    selected && Array.isArray(selected.cidades)
      ? [...selected.cidades].sort(
          (a: any, b: any) => (Number(b.quantidadeIngressos) || 0) - (Number(a.quantidadeIngressos) || 0)
        )
      : []

  return (
    <GeoCardContainer>
      <GeoCol>
        <GeoColTitle>Mapa de Vendas</GeoColTitle>
        <GeoMapRow>
          <StateScroll>
            {states.map((s: any, i: number) => (
              <StateRow key={`${s.uf}-${i}`} type="button" onClick={() => setSelected(s)}>
                <GeoStateName>{s.estado}</GeoStateName>
                <GeoStateCount>{s.quantidadeIngressos} ingressos</GeoStateCount>
              </StateRow>
            ))}
          </StateScroll>
          <GeoMapBox>
            <DashboardMapIcon width={280} height={290} maxSalesState={ufs} />
          </GeoMapBox>
        </GeoMapRow>
      </GeoCol>

      <GeoDivider />

      <GeoCol>
        <GeoColTitle>Estados</GeoColTitle>
        <DonutIndicator items={donutItems} labeled />
      </GeoCol>

      {selected && (
        <ModalOverlay onClick={() => setSelected(null)}>
          <ModalPanel onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{selected.estado}</ModalTitle>
              <ModalClose type="button" onClick={() => setSelected(null)}>
                ×
              </ModalClose>
            </ModalHead>
            <CitySub>
              Cidades por ingressos ({selected.quantidadeIngressos} no total)
            </CitySub>
            {cities.length > 0 ? (
              <CityList>
                {cities.map((c: any, i: number) => (
                  <CityRow key={`${c.nome}-${i}`}>
                    <CityRank>{i + 1}</CityRank>
                    <CityName title={c.nome}>{c.nome || '—'}</CityName>
                    <CityCount>{c.quantidadeIngressos} ing</CityCount>
                  </CityRow>
                ))}
              </CityList>
            ) : (
              <Empty>Sem detalhe de cidades para este estado.</Empty>
            )}
          </ModalPanel>
        </ModalOverlay>
      )}
    </GeoCardContainer>
  )
}

export default GeoSalesCard
